import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import {
  ChurchSeries,
  ChurchSeriesAttributesBody,
  ChurchSeriesBundle,
  ChurchSeriesPage
} from '@interfaces/series.interface';

export default class SeriesController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  // A page of a campus's series in one category, newest start date first. No
  // `categoryID` means the campus's uncategorized series, as with services.
  // Pass a page's `nextLastSeriesID` as `lastSeriesID` for the one after.
  static async fetchSeriesPage(
    churchID: string,
    campusID: string,
    categoryID: string | null,
    lastSeriesID: string | null,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchSeriesPage>> {
    const query = new URLSearchParams();

    if (categoryID) query.set('categoryID', categoryID);
    if (lastSeriesID) query.set('lastSeriesID', lastSeriesID);

    const queryString = query.toString();

    return await NetworkController.performRequestAt<ChurchSeriesPage>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/campus/${campusID}/library/series${queryString ? `?${queryString}` : ''}`
      ),
      auth.consumeRefreshToken
    );
  }

  // A series with one page of its services, newest first. Scoped to the
  // campus: a series filed under another one is a 404, like a missing one.
  // Only past services are public; `includeUpcoming` adds scheduled ones and
  // needs the Service Manager role.
  static async fetchSeries(
    churchID: string,
    campusID: string,
    seriesID: string,
    includeUpcoming: boolean,
    lastServiceID: string | null,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchSeriesBundle>> {
    const query = new URLSearchParams();

    if (includeUpcoming) query.set('includeUpcoming', 'true');
    if (lastServiceID) query.set('lastServiceID', lastServiceID);

    const queryString = query.toString();

    return await NetworkController.performRequestAt<ChurchSeriesBundle>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/campus/${campusID}/library/series/${seriesID}${queryString ? `?${queryString}` : ''}`
      ),
      auth.consumeRefreshToken
    );
  }

  // Creates a series at `attributes.campusID`. Needs the Service Manager role;
  // the thumbnail has to be one uploaded through uploadLibraryThumbnail.
  static async createSeries(
    churchID: string,
    attributes: ChurchSeriesAttributesBody,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchSeries>> {
    return await NetworkController.performRequestAt<ChurchSeries>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/campus/${attributes.campusID}/library/series`, {
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  // Replaces a series' attributes, which must be complete. The path names the
  // campus it ends up at, moving it if that's a different one; its services
  // stay in it.
  static async updateSeries(
    churchID: string,
    seriesID: string,
    attributes: ChurchSeriesAttributesBody,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchSeries>> {
    return await NetworkController.performRequestAt<ChurchSeries>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/campus/${attributes.campusID}/library/series/${seriesID}`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  // Deletes a series. Its services aren't deleted; they're taken out of it.
  static async deleteSeries(
    churchID: string,
    seriesID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<void>> {
    return await NetworkController.performRequestAt<void>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.DELETE, `/v1/church/${churchID}/library/series/${seriesID}`),
      auth.consumeRefreshToken
    );
  }
}
